import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { Sparkles } from 'lucide-react';
import { Download2Line } from '@archipro-design/icons';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useTracker } from '@archipro-website/tracker';
import { useCanDownloadImages } from '@modules/user';
import type { ImageColumnProps } from '@modules/page-builder/types';
import type { ModuleProduct } from '@modules/page-builder/api/get-module-product.server';
import type { PageBuilderGetModuleProductAction } from '~/routes/remix-api.page-builder.get-module-product';
import { QUERY_MODULE_PRODUCT } from '~/routes/remix-api.page-builder.get-module-product';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { useBoostControl } from '@modules/page-builder/component/common/upscale-warning/boost-control-context';
import { encodeDownloadLink } from '@modules/page-builder/component/detail/detail-single-image-shell/HoverButtonsImage';
import { TileActionIconButton } from '~/modules/moodboards/components/TileActionIconButton';
import { buildTileActionProps } from '~/modules/moodboards/utils/tile-action-props';

/**
 * The column's product, looked up once per `productId`.
 *
 * `product` stays undefined until this id's lookup is idle: while it loads,
 * or after the id changes, the fetcher still holds the previous payload,
 * which would otherwise render as this product. A thrown load leaves `data`
 * undefined and `state` idle, so the attempted-id guard is what stops it
 * retrying on every render.
 */
export const useModuleProduct = (column: ImageColumnProps) => {
    const fetcher = useFetcher<PageBuilderGetModuleProductAction>();
    const attemptedId = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (!column.productId) return;
        if (attemptedId.current === column.productId) return;
        attemptedId.current = column.productId;
        fetcher.load(
            `${QUERY_MODULE_PRODUCT}?productId=${encodeURIComponent(column.productId)}`,
        );
    }, [fetcher, column.productId]);

    const settled =
        !!column.productId &&
        attemptedId.current === column.productId &&
        fetcher.state === 'idle';

    return {
        product: settled ? (fetcher.data ?? undefined) : undefined,
        settled,
    };
};

/**
 * The directory's product card model for a module column.
 *
 * No price or swatches — those belong to the detail page — and the fields
 * the card does not read are left empty rather than invented.
 */
export const buildModuleProductTile = (
    column: ImageColumnProps,
    product: ModuleProduct | undefined,
): ProductTileViewModel => {
    const title = product?.productName || column.caption || '';
    return {
        id: Number(product?.id ?? column.productId ?? 0),
        title,
        // The editor's own cut-out, which is what the module is built from.
        images: column.imageUrl
            ? [
                  {
                      src: column.imageUrl,
                      alt: title,
                      // A multiplied image is a cut-out with no ground of its
                      // own, so the tile supplies one — otherwise the product
                      // floats on bare page white.
                      multiplied: column.libraryImage?.IsMultiplied,
                  },
              ]
            : [],
        video: [],
        link: product?.productLink ?? '',
        price: null,
        variants: null,
        hasAwards: false,
        draft: false,
        badge: null,
        pendingApproval: false,
        canPurchase: false,
        professional: {
            id: Number(product?.professionalId ?? 0),
            name: product?.professionalName ?? '',
            link: product?.professionalLink ?? '',
        },
        seoDescription: '',
        website: product?.website ?? null,
    };
};

/**
 * Boost and Download, in the same foot row as Share — staff tools a
 * product tile in an article carries.
 */
const ModuleProductTools = ({ column }: { column: ImageColumnProps }) => {
    const { mobile } = useAppDisplayModeContext();
    const boostControl = useBoostControl();
    const canDownload = useCanDownloadImages();
    const tracker = useTracker();
    const downloadLink = column.libraryImage?.DownloadLink;
    const photoID = column.libraryImage?.ID;

    if (mobile) return null;

    return (
        <>
            {boostControl ? (
                <TileActionIconButton
                    label={
                        boostControl.wasUpscaled
                            ? 'Compare boost'
                            : 'Boost Resolution'
                    }
                    icon={<Sparkles />}
                    onClick={boostControl.activate}
                />
            ) : null}
            {canDownload && downloadLink ? (
                <TileActionIconButton
                    label="Download"
                    icon={<Download2Line />}
                    href={encodeDownloadLink(downloadLink)}
                    download
                    onClick={() => {
                        if (!photoID) return;
                        tracker.log('StaffDownloadImage', {
                            url: new URL(window.location.href),
                            data: { LibraryImageID: photoID },
                        });
                    }}
                />
            ) : null}
        </>
    );
};

/**
 * The product's hover panel: Save as a product pin, Visit site to the
 * product's supplier, Share, and the staff tools.
 *
 * No `libraryItemId`: on a ProductPin it must be the product, and
 * `column.libraryImage.ID` is the photograph — saved state and unsave would
 * track the wrong id.
 */
export const buildModuleProductPanel = (
    column: ImageColumnProps,
    model: ProductTileViewModel,
) =>
    model.id
        ? {
              ...buildTileActionProps(model),
              shareLeading: <ModuleProductTools column={column} />,
          }
        : null;
